import { jalaliNow, toJalali, toUtc, utcNow } from "@payping/utils/dateUtils";
import { PickerRangesLocale } from "@payping/web-components/components/DatePicker/generate/getDateRanges";
import { useAtom } from "jotai";
import { dashboardPreferencesAtom } from "@/core/state";
import { selectAtom } from "jotai/utils";

const languageAtom = selectAtom(dashboardPreferencesAtom, (val) => val.language);

export type PickerRangesKeys =
  | "thisWeek"
  | "prevWeek"
  | "thisMonth"
  | "prevMonth"
  | "thisYear"
  | "prevYear"
  | "initialThisDate"
  | "initialLastDate";

const getLocaleNowMethod = (locale: PickerRangesLocale): "jalaliNow" | "utcNow" =>
  locale === "fa" ? "jalaliNow" : "utcNow";

const dateUtils = { utcNow, jalaliNow, toJalali, toUtc };

export const useDateRanges: () => Record<
  PickerRangesKeys,
  { label: string; startDate: string; endDate: string }
> = () => {
  const [language] = useAtom(languageAtom);

  const nowMethod = getLocaleNowMethod(language);
  const result: Record<PickerRangesKeys, { label: string; startDate: string; endDate: string }> = {
    initialThisDate: {
      label: "This Year",
      startDate: dateUtils[nowMethod]({ manipulate: [{ args: ["year"], method: "startOf" }] }).string,
      endDate: dateUtils[nowMethod]().string,
    },
    initialLastDate: {
      label: "Last Year",
      startDate: dateUtils[nowMethod]({
        manipulate: [
          { args: [1, "year"], method: "subtract" },
          { args: ["year"], method: "startOf" },
        ],
      }).string,
      endDate: dateUtils[nowMethod]({
        manipulate: [{ args: [1, "year"], method: "subtract" }],
      }).string,
    },
    thisWeek: {
      label: "This Week",
      startDate: dateUtils[nowMethod]({ manipulate: [{ args: ["week"], method: "startOf" }] }).string,
      endDate: dateUtils[nowMethod]().string,
    },
    prevWeek: {
      label: "Last Week",
      startDate: dateUtils[nowMethod]({
        manipulate: [
          { args: [1, "weeks"], method: "subtract" },
          { args: ["week"], method: "startOf" },
        ],
      }).string,
      endDate: dateUtils[nowMethod]({
        manipulate: [{ args: [1, "weeks"], method: "subtract" }],
      }).string,
    },
    thisMonth: {
      label: "This Month",
      startDate: dateUtils[nowMethod]({ manipulate: [{ args: ["month"], method: "startOf" }] }).string,
      endDate: dateUtils[nowMethod]().string,
    },
    prevMonth: {
      label: "Last Month",
      startDate: dateUtils[nowMethod]({
        manipulate: [
          { args: [1, "month"], method: "subtract" },
          { args: ["month"], method: "startOf" },
        ],
      }).string,
      endDate: dateUtils[nowMethod]({
        manipulate: [{ args: [1, "month"], method: "subtract" }],
      }).string,
    },
    thisYear: {
      label: "This Year",
      startDate: dateUtils[nowMethod]({ manipulate: [{ args: ["year"], method: "startOf" }] }).string,
      endDate: dateUtils[nowMethod]().string,
    },
    prevYear: {
      label: "Last Year",
      startDate: dateUtils[nowMethod]({
        manipulate: [
          { args: [1, "year"], method: "subtract" },
          { args: ["year"], method: "startOf" },
        ],
      }).string,
      endDate: dateUtils[nowMethod]({
        manipulate: [{ args: [1, "year"], method: "subtract" }],
      }).string,
    },
  };

  return result;
};
